import { useEffect, useMemo, useState } from "react";
import { useGetAllProductsWithCalculatedSalesUnits } from "../../../../api/product";
import { useGetRecipeIngredient } from "../../../../api/recipe-ingredients";
import { useGetStaticRecipeIngredientData } from "../../../../api/static-data";
import { getCompanyTenantId } from "../../../../utils/cacheStorage";
import {
  GenericIngredientOption,
  ProductUnitOption
} from "../types/recipeAddIngredient.types";
import { createCompositeKey } from "../utils/recipeAddIngredient.utils";

interface Product {
  productId: string;
  productName: string;
  salesUnits: Array<{ salesUnitId: string; salesUnitName: string }>;
}

interface UseIngredientOptionsParams {
  selectedSalesUnitIds: string[];
  selectedGenericIngredientIds: number[];
}

export function useIngredientOptions({
  selectedSalesUnitIds,
  selectedGenericIngredientIds
}: UseIngredientOptionsParams) {
  const [tenantId, setTenantId] = useState("");

  useEffect(() => {
    const companyTenantId = getCompanyTenantId();
    if (companyTenantId) setTenantId(companyTenantId);
  }, []);

  const { data: products = [], isLoading: isLoadingProducts } =
    useGetAllProductsWithCalculatedSalesUnits();

  const {
    data: staticRecipeIngredients,
    isLoading: isLoadingStaticRecipeIngredients,
    isSuccess: isStaticRecipeIngredientsSuccess
  } = useGetStaticRecipeIngredientData();

  const {
    data: allowRecipeIngredients,
    isLoading: isLoadingAllowRecipeIngredients,
    isSuccess: isAllowRecipeIngredientsSuccess
  } = useGetRecipeIngredient(tenantId);

  const allowedIngredientIds = useMemo(() => {
    if (isAllowRecipeIngredientsSuccess && Array.isArray(allowRecipeIngredients)) {
      return allowRecipeIngredients.map((item: { ingredientId: number }) => item.ingredientId);
    }
    return [];
  }, [allowRecipeIngredients, isAllowRecipeIngredientsSuccess]);

  const productUnitOptions = useMemo<ProductUnitOption[]>(() => {
    const uniqueProducts = Array.from(
      new Map((products as Product[]).map(product => [product.productId, product])).values()
    );
    const optionsMap = new Map<string, ProductUnitOption>();

    uniqueProducts.forEach(product => {
      product.salesUnits?.forEach(unit => {
        const compositeKey = createCompositeKey(product.productId, unit.salesUnitId);
        if (!optionsMap.has(compositeKey)) {
          optionsMap.set(compositeKey, {
            id: compositeKey,
            title: `${product.productName} - ${unit.salesUnitName}`,
            value: compositeKey,
            productId: product.productId,
            salesUnitId: unit.salesUnitId,
            disabled: selectedSalesUnitIds.includes(unit.salesUnitId)
          });
        }
      });
    });

    return Array.from(optionsMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  }, [products, selectedSalesUnitIds]);

  const genericOptions = useMemo<GenericIngredientOption[]>(() => {
    if (
      !isStaticRecipeIngredientsSuccess ||
      !Array.isArray(staticRecipeIngredients) ||
      !Array.isArray(allowedIngredientIds)
    ) {
      return [];
    }

    return staticRecipeIngredients
      .filter((item: { value: number }) => allowedIngredientIds.includes(Number(item.value)))
      .map(
        (item: {
          key: number;
          text: string;
          value: number;
          description: string | null;
          status: string | null;
          record: string | null;
          allocationMethod: string | null;
          databaseName: string | null;
        }) => ({
          id: Number(item.value),
          title: String(item.text),
          value: Number(item.value),
          disabled: selectedGenericIngredientIds.includes(Number(item.value)),
          description: item.description ?? null,
          status: item.status ?? null,
          record: item.record ?? null,
          allocationMethod: item.allocationMethod ?? null,
          databaseName: item.databaseName ?? null
        })
      );
  }, [
    staticRecipeIngredients,
    isStaticRecipeIngredientsSuccess,
    allowedIngredientIds,
    selectedGenericIngredientIds
  ]);

  const isLoading =
    isLoadingProducts || isLoadingStaticRecipeIngredients || isLoadingAllowRecipeIngredients;

  return {
    productUnitOptions,
    genericOptions,
    isLoading
  };
}
